import type {
  ExitPlacementPriorityLeak,
  ExitPlacementPriorityLeaksBlock,
  ManagementExplorerListResponse,
  ManagementExplorerQuery,
  ManagementExplorerSegmentKind,
  ManagementExplorerSliceRow,
  ManagementExplorerSnapshot,
  ManagementExplorerSort,
} from "../types/managementExplorer";
import type { ConditionAnalysisSummaryRow } from "./conditionAnalysisSelectors";
import {
  compareRegimeDrilldownTableKeys,
  regimeDrilldownTableKeyFromBreakdownMap,
} from "./marketContextDrilldownLabels";
import type { PolicyTreeV2, PolicyTreeV2Rule } from "../types/tradePolicy";
import { evidencePointerToParts, getEvidenceDocumentRoot, normalizeJsonPointer, resolveEvidencePointer } from "./reportEvidencePointer";
import { parseTesFocusEvidencePointer } from "./tesEvidencePointers";
import { sliceCanAddToCart, sliceHasActionableExit } from "./exitPlacementDisplay";

const TES_FOCUS_SLICE_CAP = 3;

function isLosingExplorerSlice(row: ManagementExplorerSliceRow): boolean {
  const expectancy = row.expectancy;
  if (typeof expectancy === "number" && Number.isFinite(expectancy)) {
    return expectancy < 0;
  }
  const pnl = row.total_pnl;
  if (typeof pnl === "number" && Number.isFinite(pnl)) {
    return pnl < 0;
  }
  return false;
}

function tesFocusSliceIdFromRow(item: unknown): string {
  if (typeof item === "string") return item.trim();
  if (item && typeof item === "object" && !Array.isArray(item)) {
    const o = item as Record<string, unknown>;
    return String(o.slice_id ?? o.segment_key ?? "").trim();
  }
  return "";
}

export function getExitPlacementPriorityLeaks(snapshot: unknown): ExitPlacementPriorityLeak[] {
  const doc = getEvidenceDocumentRoot(snapshot);
  if (!doc || typeof doc !== "object" || Array.isArray(doc)) return [];
  const stats = (doc as Record<string, unknown>).stats;
  if (!stats || typeof stats !== "object" || Array.isArray(stats)) return [];
  const block = (stats as Record<string, unknown>).exit_placement_priority_leaks;
  if (!block || typeof block !== "object" || Array.isArray(block)) return [];
  const leaks = (block as ExitPlacementPriorityLeaksBlock).leaks;
  if (!Array.isArray(leaks)) return [];
  const cap =
    typeof (block as ExitPlacementPriorityLeaksBlock).cap === "number"
      ? (block as ExitPlacementPriorityLeaksBlock).cap!
      : TES_FOCUS_SLICE_CAP;
  return leaks
    .filter((row): row is ExitPlacementPriorityLeak => Boolean(row) && typeof row === "object")
    .slice(0, cap);
}

export function extractManagementExplorerFromSnapshot(inputSnapshot: unknown): {
  explorer: ManagementExplorerSnapshot | null;
  slices: ManagementExplorerSliceRow[];
  tesFocusSliceIds: string[];
} {
  if (!inputSnapshot || typeof inputSnapshot !== "object" || Array.isArray(inputSnapshot)) {
    return { explorer: null, slices: [], tesFocusSliceIds: [] };
  }
  const snap = inputSnapshot as Record<string, unknown>;
  const explorerRaw = snap.management_explorer;
  const explorer =
    explorerRaw && typeof explorerRaw === "object" && !Array.isArray(explorerRaw)
      ? (explorerRaw as ManagementExplorerSnapshot)
      : null;
  const slices =
    explorer && Array.isArray(explorer.slices)
      ? (explorer.slices as ManagementExplorerSliceRow[])
      : [];

  const catalogRaw = snap.tes_focus_slice_catalog;
  const catalogIds = Array.isArray(catalogRaw)
    ? catalogRaw.map(tesFocusSliceIdFromRow).filter(Boolean)
    : [];
  const focusRaw = snap.tes_focus_slices;
  const focusIds = Array.isArray(focusRaw) ? focusRaw.map(tesFocusSliceIdFromRow).filter(Boolean) : [];
  const mergedFocus = [...catalogIds, ...focusIds.filter((id) => !catalogIds.includes(id))];
  const tesFocusSliceIds = mergedFocus.slice(0, TES_FOCUS_SLICE_CAP);

  return { explorer, slices, tesFocusSliceIds };
}

export function buildExplorerSliceId(row: ManagementExplorerSliceRow): string {
  const explicit = String(row.slice_id ?? "").trim();
  if (explicit) return explicit;
  const map = String(row.breakdown_map ?? "").trim();
  const key = String(row.segment_key ?? "").trim();
  if (!map || !key) return "";
  return `${map}:${key}`;
}

export function normalizeExplorerSlice(row: ManagementExplorerSliceRow): ManagementExplorerSliceRow {
  const slice_id = buildExplorerSliceId(row) || row.slice_id;
  return { ...row, slice_id };
}

export function explorerSegmentKey(row: ManagementExplorerSliceRow): string {
  return (
    String(row.segment_key ?? "").trim() ||
    buildExplorerSliceId(row) ||
    String(row.segment_label ?? "").trim()
  );
}

export function explorerRowToSummaryRow(row: ManagementExplorerSliceRow): ConditionAnalysisSummaryRow {
  const key = String(row.segment_key ?? "").trim() || String(row.segment_label ?? "").trim();
  return {
    key,
    sample_size: row.sample_size,
    win_rate: row.win_rate,
    expectancy: row.expectancy,
    total_pnl: row.total_pnl,
  };
}

export type ExplorerDrilldownGroup = {
  breakdownMap: string;
  tableKey: string;
  rows: ManagementExplorerSliceRow[];
  summaryRows: ConditionAnalysisSummaryRow[];
};

export function groupExplorerSlicesByBreakdownMap(
  slices: ManagementExplorerSliceRow[]
): ExplorerDrilldownGroup[] {
  const groups = new Map<string, ManagementExplorerSliceRow[]>();
  for (const row of slices) {
    const map = String(row.breakdown_map ?? "by_market_condition").trim() || "by_market_condition";
    const list = groups.get(map) ?? [];
    list.push(row);
    groups.set(map, list);
  }
  return Array.from(groups.entries())
    .map(([breakdownMap, rows]) => {
      const tableKey = regimeDrilldownTableKeyFromBreakdownMap(breakdownMap);
      return {
        breakdownMap,
        tableKey,
        rows,
        summaryRows: rows.map(explorerRowToSummaryRow).filter((r) => r.key),
      };
    })
    .filter((g) => g.summaryRows.length > 0)
    .sort((a, b) => compareRegimeDrilldownTableKeys(a.tableKey, b.tableKey));
}

function sortExplorerSlices(
  slices: ManagementExplorerSliceRow[],
  sort: ManagementExplorerSort
): ManagementExplorerSliceRow[] {
  const copy = [...slices];

  switch (sort) {
    case "expectancy_asc":
      return copy.sort(
        (a, b) => (a.expectancy ?? Number.POSITIVE_INFINITY) - (b.expectancy ?? Number.POSITIVE_INFINITY)
      );
    case "pnl_desc":
      return copy.sort((a, b) => (b.total_pnl ?? 0) - (a.total_pnl ?? 0));
    case "actionable_first":
      return copy.sort((a, b) => {
        const aa = sliceCanAddToCart(a) ? 1 : 0;
        const ab = sliceCanAddToCart(b) ? 1 : 0;
        if (ab !== aa) return ab - aa;
        return (b.sample_size ?? 0) - (a.sample_size ?? 0);
      });
    case "opportunity_score_desc":
    default:
      return copy.sort((a, b) => (b.sample_size ?? 0) - (a.sample_size ?? 0));
  }
}

export function filterExplorerSlices(
  slices: ManagementExplorerSliceRow[],
  query: ManagementExplorerQuery
): ManagementExplorerSliceRow[] {
  const minN = query.min_sample_size ?? 0;
  const kind = String(query.segment_kind ?? "").trim() as ManagementExplorerSegmentKind | "";
  const actionableExitOnly = Boolean(
    query.actionable_exit_only ?? query.actionable_only
  );

  const losingOnly = Boolean(query.losing_only);

  let out = slices.map(normalizeExplorerSlice).filter((row) => {
    const n = row.sample_size ?? 0;
    if (minN > 0 && n < minN) return false;
    if (kind && String(row.segment_kind ?? "").toLowerCase() !== kind) return false;
    if (losingOnly && !isLosingExplorerSlice(row)) return false;
    if (actionableExitOnly && !sliceHasActionableExit(row)) return false;
    return true;
  });

  out = sortExplorerSlices(out, query.sort ?? "expectancy_asc");
  return out;
}

export function isPolicyTreeV2Rule(value: unknown): value is PolicyTreeV2Rule {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const r = value as PolicyTreeV2Rule;
  return Array.isArray(r.actions);
}

export function buildPolicyTreeFromExplorerSlices(
  selected: ManagementExplorerSliceRow[]
): PolicyTreeV2 | null {
  const rules: PolicyTreeV2Rule[] = [];
  for (const row of selected) {
    if (isPolicyTreeV2Rule(row.simulation_rule)) {
      rules.push(row.simulation_rule);
    }
  }
  if (!rules.length) return null;
  return { schema_version: "policy_tree.v2", rules };
}

export function explorerListFromSnapshot(
  inputSnapshot: unknown,
  query?: ManagementExplorerQuery
): ManagementExplorerListResponse {
  const { explorer, slices } = extractManagementExplorerFromSnapshot(inputSnapshot);
  const filtered = filterExplorerSlices(slices, query ?? {});
  return {
    explorer: explorer ?? undefined,
    slices: filtered,
    slice_count: filtered.length,
    schema_version: explorer?.schema_version,
  };
}

function sliceIdFromPriorityLeakRow(value: unknown): string | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const sliceId = String((value as Record<string, unknown>).slice_id ?? "").trim();
  return sliceId || null;
}

/** Trade-management: map an evidence pointer to an explorer `slice_id` when possible. */
export function resolveExplorerSliceIdFromEvidencePointer(
  snapshot: unknown,
  pointer: string
): string | null {
  const normalized = normalizeJsonPointer(pointer);
  const tesParsed = parseTesFocusEvidencePointer(normalized);
  if (tesParsed?.mode === "by_slice_id" && tesParsed.sliceId.trim()) {
    return tesParsed.sliceId.trim();
  }

  const parts = evidencePointerToParts(pointer);
  if (parts[0] === "stats" && parts[1] === "exit_placement_priority_leaks") {
    if (parts[2] === "leaks" && parts[3] && /^\d+$/.test(parts[3])) {
      const leakPointer = `/stats/exit_placement_priority_leaks/leaks/${parts[3]}`;
      const leakRow = resolveEvidencePointer(snapshot, leakPointer);
      const fromLeak = sliceIdFromPriorityLeakRow(leakRow);
      if (fromLeak) return fromLeak;
    }
    if (parts[parts.length - 1] === "slice_id") {
      const value = resolveEvidencePointer(snapshot, normalized);
      if (typeof value === "string" && value.trim()) return value.trim();
    }
  }

  return null;
}
